import { memo, useCallback, useMemo, useState } from 'react';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableFooter from '@mui/material/TableFooter';
import TablePagination from '@mui/material/TablePagination';
import TableCell from '@mui/material/TableCell';
import TableSortLabel from '@mui/material/TableSortLabel';
import LinearProgress from '@mui/material/LinearProgress';
import Tooltip from '@mui/material/Tooltip';
import InfoIcon from '@mui/icons-material/Info';
import { ValidationRow } from './ValidationRow';
import { OBJ_APP_BRIDGES, NUM_ASSIGNMENTS } from '../_constants';
import { getReportAssignments } from '../_utils';
import { compareFn } from 'utils/objects/ObjectUtil';

import classes from './ValidationTable.module.scss';

const KEY_ASC = 'asc';
const KEY_DESC = 'desc';
const KEY_ORDER_VALID = 'validated';
const KEY_ORDER_NAME = 'name';
const KEY_ORDER_JC = 'jobCategory';
const KEY_ORDER_EXP = 'experience';
const KEY_ORDER_WAVE = 'wave';
const NUM_ROWS = 50;

export const ValidationTable = memo(function ValidationTable({
	loading = false,
	readOnly = false,
	reports = [],
	systemID = OBJ_APP_BRIDGES.ID,
	onCreateHandler = () => {},
	onDeleteHandler = () => {},
	onEditHandler = null,
	onJobCategoryHandler = () => {},
	onToggleHandler = () => {},
	onValidateHandler = () => {},
	onRowInfoClick = () => {},
}) {
	const [sSelectedRow, setSelectedRow] = useState(-1);
	const [sOrder, setOrder] = useState(KEY_ASC);
	const [sOrderBy, setOrderBy] = useState(KEY_ORDER_NAME);
	const [sPage, setPage] = useState(0);

	// ensure page local state does not become out-of-bounds when reports length changes
	const page =
		sPage > Math.floor(reports.length / NUM_ROWS)
			? Math.floor(reports.length / NUM_ROWS)
			: sPage;

	const onSortChange = useCallback((key, orderBy) => {
		// flip sort order only when column has not changed; otherwise, default to ascending
		if (orderBy === key) {
			setOrder(order => (order === KEY_ASC ? KEY_DESC : KEY_ASC));
		} else {
			setOrder(KEY_ASC);
		}
		setOrderBy(key);
	}, []);
	const compareFnName = useCallback(
		(a, b, order) => compareFn(a, b, 'name', order === KEY_ASC ? 1 : 0),
		[]
	);
	const compareFnJobCategory = useCallback(
		(a, b, order) => {
			const valueFirstEl = a.assignment.jobCategory.name;
			const valueSecondEl = b.assignment.jobCategory.name;

			if (valueFirstEl < valueSecondEl) {
				return order === KEY_ASC ? -1 : 1;
			} else if (valueFirstEl > valueSecondEl) {
				return order === KEY_ASC ? 1 : -1;
			} else {
				return compareFnName(a, b, order);
			}
		},
		[compareFnName]
	);
	const compareFnValidation = useCallback(
		(a, b, order) => {
			if (a.assignment.validated && !b.assignment.validated) {
				return order === KEY_ASC ? 1 : -1;
			} else if (!a.assignment.validated && b.assignment.validated) {
				return order === KEY_ASC ? -1 : 1;
			} else {
				return compareFnName(a, b, order);
			}
		},
		[compareFnName]
	);
	const compareFnExperience = useCallback(
		(a, b, order) => {
			if (a.assignment.experienced && !b.assignment.experienced) {
				return order === KEY_ASC ? 1 : -1;
			} else if (!a.assignment.experienced && b.assignment.experienced) {
				return order === KEY_ASC ? -1 : 1;
			} else {
				return compareFnName(a, b, order);
			}
		},
		[compareFnName]
	);
	const compareFnWave = useCallback(
		(a, b, order) => {
			if (a.assignment.wave > b.assignment.wave) {
				return order === KEY_ASC ? 1 : -1;
			} else if (a.assignment.wave < b.assignment.wave) {
				return order === KEY_ASC ? -1 : 1;
			} else {
				return compareFnName(a, b, order);
			}
		},
		[compareFnName]
	);

	const tableHead = useMemo(
		() => (
			<TableHead>
				<TableRow>
					<th />
					{onEditHandler && (
						<TableCell variant='head' align='center'>
							Edit
						</TableCell>
					)}
					<TableCell
						sx={{ paddingLeft: '12px', paddingRight: '9px' }}
						variant='head'
						align='center'
						sortDirection={
							sOrderBy === KEY_ORDER_VALID ? sOrder : false
						}
					>
						<TableSortLabel
							active={sOrderBy === KEY_ORDER_VALID}
							direction={
								sOrderBy === KEY_ORDER_VALID ? sOrder : KEY_ASC
							}
							onClick={() =>
								onSortChange(KEY_ORDER_VALID, sOrderBy)
							}
						>
							Valid
						</TableSortLabel>
					</TableCell>
					<TableCell
						variant='head'
						align='left'
						sortDirection={
							sOrderBy === KEY_ORDER_NAME ? sOrder : false
						}
					>
						<TableSortLabel
							active={sOrderBy === KEY_ORDER_NAME}
							direction={
								sOrderBy === KEY_ORDER_NAME ? sOrder : KEY_ASC
							}
							onClick={() =>
								onSortChange(KEY_ORDER_NAME, sOrderBy)
							}
						>
							Name
						</TableSortLabel>
					</TableCell>
					<TableCell
						variant='head'
						align='left'
						sortDirection={
							sOrderBy === KEY_ORDER_JC ? sOrder : false
						}
					>
						<TableSortLabel
							active={sOrderBy === KEY_ORDER_JC}
							direction={
								sOrderBy === KEY_ORDER_JC ? sOrder : KEY_ASC
							}
							onClick={() => onSortChange(KEY_ORDER_JC, sOrderBy)}
						>
							Epic Job Category
						</TableSortLabel>
					</TableCell>
					<TableCell
						variant='head'
						align='left'
						sortDirection={
							sOrderBy === KEY_ORDER_EXP ? sOrder : false
						}
					>
						<TableSortLabel
							active={sOrderBy === KEY_ORDER_EXP}
							direction={
								sOrderBy === KEY_ORDER_EXP ? sOrder : KEY_ASC
							}
							onClick={() =>
								onSortChange(KEY_ORDER_EXP, sOrderBy)
							}
						>
							Experience
							<Tooltip
								slotProps={{
									tooltip: {
										sx: {
											maxWidth: 'none',
										},
									},
								}}
								placement='top'
								title='Does the staff member utilize Epic for this job category today?'
							>
								<InfoIcon
									sx={{
										marginLeft: '4px',
									}}
									color='action'
									fontSize='small'
								/>
							</Tooltip>
						</TableSortLabel>
					</TableCell>
					<TableCell
						sx={{ maxWidth: '110px' }}
						variant='head'
						align='left'
						sortDirection={
							sOrderBy === KEY_ORDER_WAVE ? sOrder : false
						}
					>
						<TableSortLabel
							active={sOrderBy === KEY_ORDER_WAVE}
							direction={
								sOrderBy === KEY_ORDER_WAVE ? sOrder : KEY_ASC
							}
							onClick={() =>
								onSortChange(KEY_ORDER_WAVE, sOrderBy)
							}
						>
							Wave
							<Tooltip
								slotProps={{
									tooltip: {
										sx: {
											maxWidth: 'none',
										},
									},
								}}
								placement='top'
								title='What wave of training does the staff member require?'
							>
								<InfoIcon
									sx={{
										marginLeft: '4px',
									}}
									color='action'
									fontSize='small'
								/>
							</Tooltip>
						</TableSortLabel>
					</TableCell>
					<th />
				</TableRow>
			</TableHead>
		),
		[onEditHandler, sOrder, sOrderBy, onSortChange]
	);

	const arrReportsSorted = useMemo(() => {
		let arrReports =
			reports.length > NUM_ROWS
				? reports.slice(NUM_ROWS * page + 0, NUM_ROWS * page + NUM_ROWS)
				: reports.slice();
		return arrReports.sort(
			sOrderBy === KEY_ORDER_JC
				? (a, b) => compareFnJobCategory(a, b, sOrder)
				: sOrderBy === KEY_ORDER_VALID
				? (a, b) => compareFnValidation(a, b, sOrder)
				: sOrderBy === KEY_ORDER_EXP
				? (a, b) => compareFnExperience(a, b, sOrder)
				: sOrderBy === KEY_ORDER_WAVE
				? (a, b) => compareFnWave(a, b, sOrder)
				: (a, b) => compareFnName(a, b, sOrder)
		);
	}, [
		reports,
		sOrderBy,
		sOrder,
		page,
		compareFnName,
		compareFnValidation,
		compareFnJobCategory,
		compareFnExperience,
		compareFnWave,
	]);

	return (
		<TableContainer
			sx={{
				overflowX: 'unset',
			}}
		>
			<Table
				sx={{
					'& .MuiTableCell-body, .MuiTableCell-footer': {
						borderRight: '1px solid rgba(224, 224, 224, 1)',
					},
					'& td:nth-child(1 of .MuiTableCell-body, .MuiTableCell-footer)':
						{
							borderLeft: '1px solid rgba(224, 224, 224, 1)',
						},
				}}
				className={classes['table']}
				size='small'
				stickyHeader
				aria-labelledby='label--direct-reports'
			>
				{tableHead}
				<TableBody>
					{loading && (
						<TableRow>
							<TableCell
								sx={{ padding: '0' }}
								colSpan={onEditHandler ? 7 : 6}
							>
								<LinearProgress color='primary' />
							</TableCell>
						</TableRow>
					)}
					{arrReportsSorted.map(report => {
						const key = report.assignment.id;
						const isSelected =
							sSelectedRow === report.assignment.id;
						let canCreate = true;
						let canDelete = false;
						if (isSelected) {
							let arrAssignments = getReportAssignments(
								report,
								reports
							);
							canCreate = arrAssignments.length < NUM_ASSIGNMENTS;
							// check if assignment is invalid and exists another assignment with valid, primary job category
							canDelete =
								!report.assignment.validated &&
								arrAssignments.some(
									r =>
										r.assignment.jobCategory.id !== -1 &&
										r.assignment.id !==
											report.assignment.id &&
										r.assignment.jobCategory.isPrimary()
								);
						}
						return (
							<ValidationRow
								key={key}
								id={key}
								assignmentValid={report.assignment.validated}
								assignmentWave={report.assignment.wave}
								assignmentExperience={
									report.assignment.experienced
								}
								assignmentJCID={
									report.assignment.jobCategory.id
								}
								assignmentJCAppID={
									report.assignment.jobCategory.appID
								}
								assignmentJCName={
									report.assignment.jobCategory.name
								}
								assignmentJCType={
									report.assignment.jobCategory.type
								}
								assignmentJCActive={
									report.assignment.jobCategory.active
								}
								reportID={report.id}
								reportName={report.name}
								canCreate={canCreate}
								canDelete={canDelete}
								loading={loading}
								readOnly={readOnly}
								selected={isSelected}
								systemID={systemID}
								onCreate={onCreateHandler}
								onDelete={onDeleteHandler}
								onEdit={onEditHandler}
								onJobCategory={onJobCategoryHandler}
								onSelect={setSelectedRow}
								onToggle={onToggleHandler}
								onValidate={onValidateHandler}
								onIconClick={onRowInfoClick}
							/>
						);
					})}
				</TableBody>
				<TableFooter>
					<TableRow>
						<TablePagination
							sx={{
								'& .MuiTablePagination-displayedRows': {
									marginBottom: '1.75px',
								},
							}}
							count={reports.length}
							onPageChange={(_, page) => setPage(page)}
							page={page}
							rowsPerPage={NUM_ROWS}
							rowsPerPageOptions={[]}
							showFirstButton
							showLastButton
						></TablePagination>
					</TableRow>
				</TableFooter>
			</Table>
		</TableContainer>
	);
});
